package diana.soleil.design.iraniancanadienbazzar.feature.admin

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.AdminRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.CategoryRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.BusinessClaim
import diana.soleil.design.iraniancanadienbazzar.core.model.BusinessStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.Category
import diana.soleil.design.iraniancanadienbazzar.core.model.DailyPlatformCounters
import diana.soleil.design.iraniancanadienbazzar.core.model.Event
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingChangeRequest
import diana.soleil.design.iraniancanadienbazzar.core.model.PendingContentItem
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingRequest
import diana.soleil.design.iraniancanadienbazzar.core.model.ModerationAction
import diana.soleil.design.iraniancanadienbazzar.core.model.ModerationActionType
import diana.soleil.design.iraniancanadienbazzar.core.model.Report
import diana.soleil.design.iraniancanadienbazzar.core.model.UserAccount
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.launch

/** Immutable UI state for the admin/moderation console (UDF). */
data class AdminUiState(
    val loading: Boolean = true,
    /** The signed-in actor's role (`admin`/`moderator`/…) — the screen scopes its sections on this. */
    val role: String? = null,
    val pending: List<Business> = emptyList(),
    /** Approved listings the owner edited post-approval (BIZ-5) — live, awaiting admin re-review. */
    val edited: List<Business> = emptyList(),
    val claims: List<BusinessClaim> = emptyList(),
    val pendingEvents: List<Event> = emptyList(),
    val reports: List<Report> = emptyList(),
    val listingRequests: List<ListingRequest> = emptyList(),
    /** Owners' pending name/category/address changes — nothing goes live until one is applied. */
    val listingChanges: List<ListingChangeRequest> = emptyList(),
    /** Hidden news/jokes/recipes/facts (daily AI job, imports) waiting to be published or discarded. */
    val pendingContent: List<PendingContentItem> = emptyList(),
    /** Append-only "who moderated what" audit trail, newest first. */
    val moderationLog: List<ModerationAction> = emptyList(),
    val error: Boolean = false,
    /** Ids with an in-flight approve/reject/resolve so their buttons render busy exactly once. */
    val busyIds: Set<String> = emptySet(),
    /** Directory categories — the overview's listings total and the queue table's category labels. */
    val categories: List<Category> = emptyList(),
    /** Every member account (admin-only list read); populated on Expanded's Users section. */
    val users: List<UserAccount> = emptyList(),
    /** The signed-in actor's own uid — role toggles on their own row are disabled (no self-demotion). */
    val currentUid: String? = null,
    /** Functions-maintained daily growth buckets (accounts + listings created per day), oldest first. */
    val growth: List<DailyPlatformCounters> = emptyList(),
    /** Recent listings of any status — the admin "All listings" browser (searched client-side). */
    val allBusinesses: List<Business> = emptyList(),
    /** The All-listings search box text (lives in the VM so a server-side search can follow it). */
    val listingQuery: String = "",
    /** Whole-directory name matches for [listingQuery] (empty when the query is blank). */
    val searchedBusinesses: List<Business> = emptyList(),
) {
    /** Total approved directory listings (Functions-maintained per-category aggregates). */
    val listingsTotal: Int get() = categories.sumOf { it.businessCount }

    /** Full admin — sees every console section (moderators see only the reports queue + audit log). */
    val isAdmin: Boolean get() = role == UserAccount.ROLE_ADMIN
}

/**
 * Drives [AdminScreen] off [AdminRepository], scoped to the signed-in actor's role: full **admins**
 * see the whole console (approvals, claims, users, campaigns, reports); **moderators** see only the
 * reports queue + audit log (the admin-only queues would permission-deny for them, so those streams
 * are simply not subscribed). Firestore rules enforce all of this server-side regardless.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class AdminViewModel(
    private val admin: AdminRepository,
    private val categoryRepository: CategoryRepository,
) : ViewModel() {

    private val busyIds = MutableStateFlow<Set<String>>(emptySet())
    private val listingQuery = MutableStateFlow("")

    // Whole-directory name search that follows the search box; the recent window the browser
    // shows by default misses older seeds, and the offer composer needs to find any business.
    @OptIn(FlowPreview::class)
    private val searchResults: Flow<List<Business>> =
        listingQuery.debounce(SEARCH_DEBOUNCE_MS).flatMapLatest { q ->
            if (q.isBlank()) flowOf(emptyList())
            else admin.searchBusinessesByName(q).map { (it as? DataResult.Success)?.data ?: emptyList() }
        }

    // The text box must echo keystrokes immediately (a controlled field that lags the debounced
    // search drops characters), so the raw query rides alongside the debounced results.
    private val listingSearch: Flow<Pair<String, List<Business>>> =
        combine(listingQuery, searchResults) { q, results -> q to results }

    fun setListingQuery(query: String) {
        listingQuery.value = query
    }

    val state: StateFlow<AdminUiState> = admin.observeMyRole().flatMapLatest { role ->
        val isAdmin = role == UserAccount.ROLE_ADMIN
        if (!UserAccount.canModerate(role)) {
            // Not privileged (shouldn't happen — Profile gates the entry) — render an empty error card.
            return@flatMapLatest flowOf(AdminUiState(loading = false, error = true, role = role))
        }
        // Admin-only queues permission-deny for a moderator; feed them empty so the state still builds.
        fun <T> adminOnly(stream: () -> Flow<DataResult<List<T>>>): Flow<DataResult<List<T>>> =
            if (isAdmin) stream() else flowOf(DataResult.Success(emptyList<T>()))

        val queues = combine(
            adminOnly { admin.observePendingBusinesses() },
            adminOnly { admin.observeClaims() },
            adminOnly { admin.observePendingEvents() },
            admin.observeOpenReports(),
            adminOnly { admin.observeListingRequests() },
        ) { pending, claims, events, reports, listingRequests ->
            arrayOf(pending, claims, events, reports, listingRequests)
        }
        // The edited-listings and change-request queues ride together so the 5-flow combine below
        // keeps its typed overload.
        val editedQueue = combine(
            adminOnly { admin.observeEditedBusinesses() },
            adminOnly { admin.observePendingListingChanges() },
            adminOnly { admin.observePendingContent() },
        ) { edited, changes, content -> Triple(edited, changes, content) }
        val aux = combine(
            categoryRepository.observeCategories(),
            adminOnly { admin.observeUsers() },
            adminOnly { admin.observeGrowthDaily() },
            admin.observeModerationLog(),
            adminOnly { admin.observeAllBusinesses() },
        ) { categories, users, growth, moderationLog, allBusinesses ->
            arrayOf(categories, users, growth, moderationLog, allBusinesses)
        }
        combine(queues, editedQueue, aux, busyIds, listingSearch) { q, editedPair, a, busy, search ->
            val (pending, claims, events, reports, listingRequests) = q
            val (editedResult, changesResult, contentResult) = editedPair
            // A real failure on one of the queues the current role actually reads → surface the error.
            val failed = q.any { it is DataResult.Failure } || a.any { it is DataResult.Failure } ||
                editedResult is DataResult.Failure || changesResult is DataResult.Failure ||
                contentResult is DataResult.Failure
            if (failed) {
                AdminUiState(loading = false, error = true, role = role, currentUid = admin.currentUid)
            } else {
                @Suppress("UNCHECKED_CAST")
                AdminUiState(
                    loading = false,
                    role = role,
                    pending = (pending as DataResult.Success<List<Business>>).data,
                    edited = (editedResult as DataResult.Success<List<Business>>).data,
                    claims = (claims as DataResult.Success<List<BusinessClaim>>).data,
                    pendingEvents = (events as DataResult.Success<List<Event>>).data,
                    reports = (reports as DataResult.Success<List<Report>>).data,
                    listingRequests = (listingRequests as DataResult.Success<List<ListingRequest>>).data,
                    listingChanges = (changesResult as DataResult.Success<List<ListingChangeRequest>>).data,
                    pendingContent = (contentResult as DataResult.Success<List<PendingContentItem>>).data,
                    moderationLog = (a[3] as DataResult.Success<List<ModerationAction>>).data,
                    busyIds = busy,
                    categories = (a[0] as? DataResult.Success<List<Category>>)?.data ?: emptyList(),
                    users = (a[1] as? DataResult.Success<List<UserAccount>>)?.data ?: emptyList(),
                    currentUid = admin.currentUid,
                    growth = (a[2] as? DataResult.Success<List<DailyPlatformCounters>>)?.data ?: emptyList(),
                    allBusinesses = (a[4] as? DataResult.Success<List<Business>>)?.data ?: emptyList(),
                    listingQuery = search.first,
                    searchedBusinesses = search.second,
                )
            }
        }
    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), AdminUiState())

    /**
     * Sets a member's role (`user`/`moderator`/`admin`). Self-demotion is blocked (the actor can't
     * drop their own admin) — the UI also disables the control on their own row.
     */
    fun setUserRole(uid: String, role: String) {
        if (uid == admin.currentUid && role != UserAccount.ROLE_ADMIN) return
        viewModelScope.launch {
            busyIds.value += uid
            admin.setUserRole(uid, role)
            busyIds.value -= uid
        }
    }

    fun approve(businessId: String) = setStatus(businessId, BusinessStatus.Approved)

    /** [reason] (optional, BIZ-6) reaches the submitter's rejection notification via the doc. */
    fun reject(businessId: String, reason: String? = null) =
        setStatus(businessId, BusinessStatus.Rejected, reason)

    fun resolveReport(reportId: String) {
        viewModelScope.launch {
            busyIds.value += reportId
            admin.resolveReport(reportId)
            busyIds.value -= reportId
        }
    }

    fun resolveListingRequest(requestId: String) {
        viewModelScope.launch {
            busyIds.value += requestId
            admin.resolveListingRequest(requestId)
            busyIds.value -= requestId
        }
    }

    /**
     * Grant a "take my listing down" request: suspend the business (reversible — it leaves the public
     * directory but the doc survives, mirroring how a business Report is actioned), log it to the audit
     * trail, then resolve the request. Rejecting a removal is just [resolveListingRequest] (the listing
     * stays up, the request is closed). Only meaningful for [ListingRequestType.Removal].
     */
    fun approveListingRemoval(request: ListingRequest) {
        viewModelScope.launch {
            busyIds.value += request.id
            val acted = admin.setBusinessStatus(request.businessId, BusinessStatus.Suspended)
            if (acted is DataResult.Success) {
                admin.logModerationAction(
                    ModerationActionType.Remove.wire, "business", request.businessId, request.type.name,
                )
                admin.resolveListingRequest(request.id)
            }
            busyIds.value -= request.id
        }
    }

    private fun setStatus(businessId: String, status: BusinessStatus, reason: String? = null) {
        viewModelScope.launch {
            busyIds.value += businessId
            admin.setBusinessStatus(businessId, status, reason)
            // Success removes the doc from the pending query; failure re-enables the row's buttons.
            busyIds.value -= businessId
        }
    }

    fun approveEvent(eventId: String) = eventStatus(eventId, approved = true)

    /** [reason] (optional, BIZ-6) reaches the organizer's rejection notification via the doc. */
    fun rejectEvent(eventId: String, reason: String? = null) =
        eventStatus(eventId, approved = false, reason = reason)

    private fun eventStatus(eventId: String, approved: Boolean, reason: String? = null) {
        viewModelScope.launch {
            busyIds.value += eventId
            admin.setEventStatus(eventId, approved, reason)
            busyIds.value -= eventId
        }
    }

    fun approveClaim(claim: BusinessClaim) {
        viewModelScope.launch {
            busyIds.value += claim.id
            admin.approveClaim(claim)
            busyIds.value -= claim.id
        }
    }

    fun rejectClaim(claim: BusinessClaim) {
        viewModelScope.launch {
            busyIds.value += claim.id
            admin.rejectClaim(claim)
            busyIds.value -= claim.id
        }
    }

    /** Applies an owner's name/category/address change to the live listing and closes the request. */
    fun approveListingChange(request: ListingChangeRequest) {
        viewModelScope.launch {
            busyIds.value += request.id
            admin.approveListingChange(request)
            busyIds.value -= request.id
        }
    }

    fun publishContent(item: PendingContentItem) {
        viewModelScope.launch {
            busyIds.value += item.id
            admin.publishContent(item)
            busyIds.value -= item.id
        }
    }

    fun discardContent(item: PendingContentItem) {
        viewModelScope.launch {
            busyIds.value += item.id
            admin.discardContent(item)
            busyIds.value -= item.id
        }
    }

    fun rejectListingChange(request: ListingChangeRequest, reason: String?) {
        viewModelScope.launch {
            busyIds.value += request.id
            admin.rejectListingChange(request, reason)
            busyIds.value -= request.id
        }
    }

    /** True if this report's target can be removed by the current actor (business needs full admin). */
    fun canRemove(report: Report, isAdmin: Boolean): Boolean = when (report.targetType) {
        "review", "post", "marketplace_item" -> true
        "business" -> isAdmin
        else -> false
    }

    /** True if this report's target can be restored (only soft-removable content; posts are hard-deleted). */
    fun canRestore(report: Report, isAdmin: Boolean): Boolean = when (report.targetType) {
        "review", "marketplace_item" -> true
        "business" -> isAdmin
        else -> false
    }

    /** Moderation: remove the report's target (by type), log it to the audit trail, then resolve. */
    fun removeReportTarget(report: Report, reason: String? = null) {
        viewModelScope.launch {
            busyIds.value += report.id
            val acted = when (report.targetType) {
                "review" -> admin.hideReview(report.targetId)
                "post" -> admin.removePost(report.targetId)
                "marketplace_item" -> admin.removeMarketplaceItem(report.targetId)
                "business" -> admin.setBusinessStatus(report.targetId, BusinessStatus.Suspended)
                else -> null
            }
            if (acted is DataResult.Success) {
                admin.logModerationAction(ModerationActionType.Remove.wire, report.targetType, report.targetId, reason)
                admin.resolveReport(report.id)
            }
            busyIds.value -= report.id
        }
    }

    /** Moderation: restore (un-hide) the report's target, log it, then resolve. */
    fun restoreReportTarget(report: Report, reason: String? = null) {
        viewModelScope.launch {
            busyIds.value += report.id
            val acted = when (report.targetType) {
                "review" -> admin.restoreReview(report.targetId)
                "marketplace_item" -> admin.restoreMarketplaceItem(report.targetId)
                "business" -> admin.setBusinessStatus(report.targetId, BusinessStatus.Approved)
                else -> null
            }
            if (acted is DataResult.Success) {
                admin.logModerationAction(ModerationActionType.Restore.wire, report.targetType, report.targetId, reason)
                admin.resolveReport(report.id)
            }
            busyIds.value -= report.id
        }
    }

    /** Moderation: resolve a report without acting on the target (nothing wrong with the content). */
    fun dismissReport(report: Report) {
        viewModelScope.launch {
            busyIds.value += report.id
            admin.logModerationAction(ModerationActionType.Dismiss.wire, report.targetType, report.targetId, null)
            admin.resolveReport(report.id)
            busyIds.value -= report.id
        }
    }
}

private const val SEARCH_DEBOUNCE_MS = 250L
